/* BRIZE Signature · bz-theme.js · socle [GABARITS]
   Chargé en module ES (différé) dans layout/theme.liquid, AVANT tout autre script de section.
   Expose window.Brize (API) ; la configuration Liquid est dans window.BZ (posée en ligne dans le <head>).
   Aucune bibliothèque. Aucun accès localStorage sans try/catch. */

const doc = document;
const root = doc.documentElement;
const BZ = window.BZ || {};
const mqReduit = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- Événements ---------- */
function emit(name, detail = {}, target = doc) {
  target.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
}
function on(name, fn, target = doc) {
  target.addEventListener(name, fn);
  return () => target.removeEventListener(name, fn);
}

/* ---------- Utilitaires ---------- */
function debounce(fn, ms = 150) {
  let t;
  return function (...args) { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), ms); };
}
function rafThrottle(fn) {
  let busy = false;
  return function (...args) {
    if (busy) return;
    busy = true;
    requestAnimationFrame(() => { busy = false; fn.apply(this, args); });
  };
}
function idle(fn, timeout = 1500) {
  const run = () => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout }) : setTimeout(fn, 200));
  if (doc.readyState === 'complete') run(); else window.addEventListener('load', run, { once: true });
}
function onVisible(el, fn, { rootMargin = '0px', threshold = 0, once = true } = {}) {
  if (!el) return () => {};
  if (!('IntersectionObserver' in window)) { fn(el); return () => {}; }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      fn(e.target, e);
      if (once) io.unobserve(e.target);
    });
  }, { rootMargin, threshold });
  io.observe(el);
  return () => io.disconnect();
}

/* fetch JSON : lève une Error { status, data, message } lisible si la réponse n'est pas 2xx */
async function fetchJSON(url, { method = 'GET', body, headers = {}, signal } = {}) {
  const opts = { method, signal, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...headers } };
  if (body !== undefined) {
    if (body instanceof FormData) opts.body = body;
    else { opts.body = JSON.stringify(body); opts.headers['Content-Type'] = 'application/json'; }
  }
  const res = await fetch(url, opts);
  let data = null;
  const text = await res.text();
  try { data = text ? JSON.parse(text) : null; } catch (_) { data = text; }
  if (!res.ok) {
    const err = new Error((data && (data.description || data.message)) || `HTTP ${res.status}`);
    err.status = res.status; err.data = data;
    throw err;
  }
  return data;
}

/* Section Rendering API : renvoie { idSection: html } */
async function sections(ids, url = window.location.pathname) {
  const list = Array.isArray(ids) ? ids.join(',') : ids;
  const sep = url.includes('?') ? '&' : '?';
  return fetchJSON(`${url}${sep}sections=${encodeURIComponent(list)}`);
}

/* ---------- Argent (format de la boutique, ex. « 119,90 € ») ---------- */
function formatMoney(cents, format = BZ.moneyFormat || '{{amount_with_comma_separator}} €') {
  const n = typeof cents === 'string' ? parseInt(cents.replace(/\D/g, ''), 10) : Math.round(cents);
  const value = Number.isFinite(n) ? n : 0;
  const fmt = (num, dec, thousands = '.', decimal = ',') => {
    const fixed = (num / 100).toFixed(dec);
    const [ent, frac] = fixed.split('.');
    const e = ent.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? e + decimal + frac : e;
  };
  return String(format).replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount': return fmt(value, 2, ',', '.');
      case 'amount_no_decimals': return fmt(value, 0, ',', '.');
      case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
      case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator': return fmt(value, 0, ' ', ',');
      case 'amount_with_period_and_space_separator': return fmt(value, 2, ' ', '.');
      case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
      default: return fmt(value, 2, '.', ',');
    }
  }).replace(/<[^>]*>/g, '');
}

/* ---------- Annonces pour lecteurs d'écran (#bz-live dans le layout) ---------- */
function announce(message) {
  const live = doc.getElementById('bz-live');
  if (!live) return;
  live.textContent = '';
  setTimeout(() => { live.textContent = message; }, 60);
}

/* ---------- Réduction des animations ---------- */
const motion = {
  isReduced() { return root.dataset.motion === 'reduit' || mqReduit.matches; },
  get() { return root.dataset.motion === 'reduit' ? 'reduit' : 'full'; },
  set(value) {
    const m = value === 'reduit' ? 'reduit' : 'full';
    if (m === 'reduit') root.dataset.motion = 'reduit'; else delete root.dataset.motion;
    try { if (m === 'reduit') localStorage.setItem('bz-motion', 'reduit'); else localStorage.removeItem('bz-motion'); } catch (_) { /* stockage bloqué */ }
    BZ.motion = m;
    syncMotionButtons();
    if (m === 'reduit') revealAllNow();
    emit('bz:motion:change', { motion: m });
  },
  toggle() { this.set(this.get() === 'reduit' ? 'full' : 'reduit'); },
};
function syncMotionButtons() {
  const reduit = motion.get() === 'reduit';
  doc.querySelectorAll('[data-bz-motion-toggle]').forEach((btn) => {
    // Le libellé change avec l'état : pas d'aria-pressed en plus (annonce contradictoire).
    btn.removeAttribute('aria-pressed');
    btn.dataset.bzMotionEtat = reduit ? 'reduit' : 'full';
    const label = reduit ? btn.dataset.labelActiver : btn.dataset.labelReduire;
    const target = btn.querySelector('[data-bz-motion-label]') || btn;
    if (label) target.textContent = label;
  });
}
mqReduit.addEventListener?.('change', () => {
  emit('bz:motion:change', { motion: motion.isReduced() ? 'reduit' : 'full' });
  if (mqReduit.matches) revealAllNow();
});

/* ---------- Apparitions au défilement ---------- */
let revealIO = null;
function splitLines(el) {
  if (el.dataset.bzSplit) return;
  el.dataset.bzSplit = '1';
  // Lecteurs d'écran : le titre se lit d'un seul tenant, les mots découpés sont masqués.
  const accessible = !el.querySelector('a, button, input, select, textarea, [tabindex]');
  if (accessible && !el.hasAttribute('aria-label')) el.setAttribute('aria-label', el.textContent.replace(/\s+/g, ' ').trim());
  const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const texts = [];
  while (walker.nextNode()) texts.push(walker.currentNode);
  texts.forEach((node) => {
    const parts = node.textContent.split(/(\s+)/);
    if (parts.length <= 1 && !node.textContent.trim()) return;
    const frag = doc.createDocumentFragment();
    parts.forEach((p) => {
      if (!p) return;
      if (/^\s+$/.test(p)) { frag.appendChild(doc.createTextNode(p)); return; }
      const w = doc.createElement('span'); w.className = 'bz-w';
      if (accessible) w.setAttribute('aria-hidden', 'true');
      const i = doc.createElement('span'); i.className = 'bz-w__i'; i.textContent = p;
      w.appendChild(i); frag.appendChild(w);
    });
    node.parentNode.replaceChild(frag, node);
  });
  measureLines(el);
}
function measureLines(el) {
  let line = -1; let lastTop = null;
  el.querySelectorAll('.bz-w').forEach((w) => {
    const top = Math.round(w.getBoundingClientRect().top);
    if (lastTop === null || Math.abs(top - lastTop) > 4) { line += 1; lastTop = top; }
    w.style.setProperty('--bz-l', String(line));
  });
}
function revealAllNow(scope = doc) {
  scope.querySelectorAll('[data-bz-reveal]').forEach((el) => el.classList.add('bz-in'));
}
function scanReveals(scope = doc) {
  const els = scope.querySelectorAll('[data-bz-reveal]:not(.bz-in)');
  if (!els.length) return;
  if (motion.isReduced() || !('IntersectionObserver' in window) || root.classList.contains('bz-design')) { revealAllNow(scope); return; }
  if (!revealIO) {
    revealIO = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('bz-in');
        revealIO.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
  }
  const lines = [];
  els.forEach((el) => {
    if (el.dataset.bzReveal === 'lines') lines.push(el); else revealIO.observe(el);
  });
  if (lines.length) {
    const go = () => lines.forEach((el) => { splitLines(el); revealIO.observe(el); });
    const fontsReady = doc.fonts && doc.fonts.ready ? Promise.race([doc.fonts.ready, new Promise((r) => setTimeout(r, 1200))]) : Promise.resolve();
    fontsReady.then(go);
  }
}

/* ---------- Header : défilement + ciel sous le header ---------- */
let cielIO = null;
function headerHeight() {
  const h = doc.querySelector('[data-bz-header]');
  return h ? Math.round(h.getBoundingClientRect().height) : 56;
}
function watchSky() {
  if (cielIO) cielIO.disconnect();
  if (!('IntersectionObserver' in window)) return;
  const h = headerHeight();
  const ligne = Math.max(1, Math.round(h / 2));
  const vh = window.innerHeight;
  const visibles = new Set();
  cielIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) visibles.add(e.target); else visibles.delete(e.target); });
    let ciel = 'matin';
    visibles.forEach((el) => { ciel = el.dataset.ciel || ciel; });
    root.dataset.bzHeaderCiel = (ciel === 'nuit' || ciel === 'abysse') ? 'nuit' : 'clair';
  }, { rootMargin: `-${ligne}px 0px -${Math.max(0, vh - ligne - 1)}px 0px`, threshold: 0 });
  doc.querySelectorAll('main [data-ciel], footer [data-ciel], [data-bz-footer][data-ciel]').forEach((el) => cielIO.observe(el));
}
function watchScroll() {
  const set = rafThrottle(() => {
    const y = window.scrollY;
    if (y > 4) root.dataset.bzScrolled = ''; else delete root.dataset.bzScrolled;
  });
  window.addEventListener('scroll', set, { passive: true });
  set();
  const header = doc.querySelector('[data-bz-header]');
  if (header && 'ResizeObserver' in window) {
    new ResizeObserver(debounce(() => {
      root.style.setProperty('--bz-header-reel', `${headerHeight()}px`);
      watchSky();
    }, 100)).observe(header);
  }
  window.addEventListener('resize', debounce(watchSky, 200), { passive: true });
}

/* ---------- Défilement verrouillé (tiroirs, menu) ---------- */
let locks = 0;
function lockScroll(lock) {
  locks = Math.max(0, locks + (lock ? 1 : -1));
  if (locks > 0) {
    root.style.setProperty('--bz-barre-defilement', `${window.innerWidth - root.clientWidth}px`);
    root.classList.add('bz-verrou');
  } else {
    root.classList.remove('bz-verrou');
  }
}

/* ---------- Piège de focus ---------- */
const FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),iframe,[tabindex]:not([tabindex="-1"]),[contenteditable="true"],summary';
function focusables(container) {
  return [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === doc.activeElement);
}
function trapFocus(container, { initial } = {}) {
  const handler = (e) => {
    if (e.key !== 'Tab') return;
    const items = focusables(container);
    if (!items.length) { e.preventDefault(); return; }
    const first = items[0]; const last = items[items.length - 1];
    if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  container.addEventListener('keydown', handler);
  const target = initial || container.querySelector('[autofocus]') || focusables(container)[0] || container;
  if (target === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
  requestAnimationFrame(() => target.focus({ preventScroll: true }));
  return () => container.removeEventListener('keydown', handler);
}

/* ---------- Tiroirs / panneaux ----------
   Balisage : <div id="x" data-bz-panel hidden role="dialog" aria-modal="true" aria-labelledby="…">
                <div data-bz-overlay></div> … <button data-bz-close>…</button></div>
   Ouvrir : bouton [data-bz-open="x"] (aria-controls="x" aria-expanded) ou Brize.panel.open(el, { trigger }).
   Classe d'état : .bz-ouvert (posée une image après hidden=false pour permettre la transition CSS).
   <dialog> natif : showModal()/close() (focus piégé par le navigateur). */
const openPanels = new Map();
const panel = {
  open(el, { trigger = doc.activeElement, initial } = {}) {
    if (typeof el === 'string') el = doc.getElementById(el);
    if (!el || openPanels.has(el)) return;
    const state = { trigger, release: null };
    openPanels.set(el, state);
    if (el instanceof HTMLDialogElement) {
      el.showModal();
      el.addEventListener('close', () => panel.afterClose(el), { once: true });
    } else {
      clearTimeout(el._bzFerme);
      el.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('bz-ouvert')));
      state.release = trapFocus(el, { initial });
    }
    lockScroll(true);
    doc.querySelectorAll(`[aria-controls="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    emit('bz:panel:open', { id: el.id }, el);
  },
  close(el) {
    if (typeof el === 'string') el = doc.getElementById(el);
    if (!el || !openPanels.has(el)) return;
    if (el instanceof HTMLDialogElement) { el.close(); return; }
    el.classList.remove('bz-ouvert');
    const d = motion.isReduced() ? 0 : parseFloat(getComputedStyle(el).getPropertyValue('--bz-panel-duree')) || 380;
    clearTimeout(el._bzFerme);
    // Réouverture pendant la transition de fermeture : open() annule ce minuteur.
    el._bzFerme = setTimeout(() => { if (!openPanels.has(el)) el.hidden = true; }, d);
    panel.afterClose(el);
  },
  afterClose(el) {
    const state = openPanels.get(el);
    if (!state) return;
    openPanels.delete(el);
    if (state.release) state.release();
    lockScroll(false);
    doc.querySelectorAll(`[aria-controls="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (state.trigger && typeof state.trigger.focus === 'function' && doc.contains(state.trigger)) state.trigger.focus({ preventScroll: true });
    emit('bz:panel:close', { id: el.id }, el);
  },
  toggle(el, opts) {
    if (typeof el === 'string') el = doc.getElementById(el);
    if (openPanels.has(el)) panel.close(el); else panel.open(el, opts);
  },
  isOpen(el) { return openPanels.has(typeof el === 'string' ? doc.getElementById(el) : el); },
  closeTop() { const last = [...openPanels.keys()].pop(); if (last) panel.close(last); },
};

/* ---------- Panier : aides bas niveau (le chantier [PANIER] possède le tiroir) ---------- */
const cart = {
  get() { return fetchJSON(`${BZ.routes.cart}.js`); },
  add(payload) { return fetchJSON(BZ.routes.cartAdd, { method: 'POST', body: payload }); },
  change(payload) { return fetchJSON(BZ.routes.cartChange, { method: 'POST', body: payload }); },
  update(payload) { return fetchJSON(BZ.routes.cartUpdate, { method: 'POST', body: payload }); },
  setCount(n) {
    doc.querySelectorAll('[data-bz-cart-count]').forEach((el) => {
      el.textContent = String(n);
      el.dataset.count = String(n);
    });
  },
  /* À appeler par [PANIER] après toute modification : met à jour le compteur et émet
     bz:cart:changed (canonique) + bz:cart:updated (alias). */
  changed(cartData) {
    if (cartData && typeof cartData.item_count === 'number') cart.setCount(cartData.item_count);
    emit('bz:cart:changed', { cart: cartData });
    emit('bz:cart:updated', { cart: cartData });
  },
};

/* ---------- Chargement différé de modules ---------- */
const modules = new Map();
function load(src) {
  if (!modules.has(src)) modules.set(src, import(src));
  return modules.get(src);
}
/* Charge un module quand l'élément approche de l'écran (une fois) */
function loadWhenVisible(el, src, { rootMargin = '100% 0px' } = {}) {
  return new Promise((resolve, reject) => {
    onVisible(el, () => load(src).then(resolve, reject), { rootMargin });
  });
}

/* ---------- View Transitions (même document) ---------- */
function transition(fn) {
  if (!doc.startViewTransition || motion.isReduced()) { fn(); return Promise.resolve(); }
  return doc.startViewTransition(fn).finished.catch(() => {});
}

/* ---------- Câblage global par attributs ---------- */
function wire() {
  doc.addEventListener('click', (e) => {
    const cartToggle = e.target.closest('[data-bz-cart-toggle]');
    if (cartToggle && doc.querySelector('[data-bz-cart-drawer]') && window.Brize && window.Brize.panier && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      emit('bz:cart:open', { trigger: cartToggle });
      return;
    }
    const opener = e.target.closest('[data-bz-open]');
    if (opener) { e.preventDefault(); panel.toggle(opener.dataset.bzOpen, { trigger: opener }); return; }
    const closer = e.target.closest('[data-bz-close], [data-bz-overlay]');
    if (closer) {
      const p = closer.closest('[data-bz-panel], dialog');
      if (p) { e.preventDefault(); panel.close(p); }
      return;
    }
    const motionBtn = e.target.closest('[data-bz-motion-toggle]');
    if (motionBtn) { e.preventDefault(); motion.toggle(); }
  });
  doc.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openPanels.size) {
      const last = [...openPanels.keys()].pop();
      if (!(last instanceof HTMLDialogElement)) { e.preventDefault(); panel.close(last); }
    }
  });
  /* Éditeur de thème Shopify */
  doc.addEventListener('shopify:section:load', (e) => { scanReveals(e.target); revealAllNow(e.target); watchSky(); emit('bz:section:load', { id: e.detail && e.detail.sectionId }, e.target); });
  doc.addEventListener('shopify:section:unload', () => setTimeout(watchSky, 50));
}

/* ---------- API publique ---------- */
const Brize = {
  version: '1.0.0',
  config: BZ,
  emit, on, debounce, rafThrottle, idle, onVisible,
  fetchJSON, sections, formatMoney, announce,
  motion, panel, trapFocus, lockScroll, cart,
  load, loadWhenVisible, transition,
  reveal: { scan: scanReveals, all: revealAllNow, splitLines, measureLines },
  ready(fn) { if (Brize.isReady) fn(Brize); else doc.addEventListener('bz:ready', () => fn(Brize), { once: true }); },
  isReady: false,
};
window.Brize = Brize;

/* ---------- Démarrage ---------- */
function init() {
  root.classList.add('bz-reveal-on');
  if (window.Shopify && window.Shopify.designMode) root.classList.add('bz-design');
  BZ.motion = motion.get();
  syncMotionButtons();
  wire();
  watchScroll();
  watchSky();
  scanReveals();
  Brize.isReady = true;
  emit('bz:ready', { Brize });
}
if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init, { once: true }); else init();

export default Brize;
